import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import Portal from '../components/Portal';
import '../components/Modal.css';
import './LivroCaixaPage.css';

const fmt = v => (v || 0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
const fmtData = d => new Date(d + 'T00:00:00').toLocaleDateString('pt-BR');

export default function LivroCaixaPage() {
  const { user, profile } = useAuth();
  const [lancamentos, setLancamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editLanc, setEditLanc] = useState(null);
  const [msg, setMsg] = useState({ type:'', text:'' });
  const [accessibleIds, setAccessibleIds] = useState([]);

  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [busca, setBusca] = useState('');

  useEffect(() => {
    if (!profile) return;
    const ids = [profile.id];
    if (profile.shared_with) ids.push(profile.shared_with);
    supabase.from('profiles').select('id').eq('shared_with', profile.id).then(({ data }) => {
      if (data) data.forEach(p => { if (!ids.includes(p.id)) ids.push(p.id); });
      setAccessibleIds(ids);
    });
  }, [profile]);

  const loadData = useCallback(async () => {
    if (!accessibleIds.length) return;
    setLoading(true);
    const { data } = await supabase
      .from('livro_caixa')
      .select('*')
      .in('owner_id', accessibleIds)
      .order('data', { ascending: true });
    setLancamentos(data || []);
    setLoading(false);
  }, [accessibleIds]);

  useEffect(() => { loadData(); }, [loadData]);

  function flash(type, text) {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type:'', text:'' }), 4000);
  }

  async function handleSave(form) {
    const payload = {
      data: form.data,
      descricao: form.descricao.trim(),
      tipo: form.tipo,
      valor: parseFloat(form.valor),
      categoria: form.categoria?.trim() || null,
      is_projecao: form.is_projecao,
    };
    if (editLanc) {
      await supabase.from('livro_caixa').update(payload).eq('id', editLanc.id);
      flash('success', 'Lançamento atualizado!');
    } else {
      await supabase.from('livro_caixa').insert({ ...payload, owner_id: user.id });
      flash('success', 'Lançamento registrado!');
    }
    setShowModal(false);
    setEditLanc(null);
    await loadData();
  }

  async function handleDelete(id) {
    if (!window.confirm('Excluir este lançamento?')) return;
    await supabase.from('livro_caixa').delete().eq('id', id);
    flash('success', 'Lançamento excluído.');
    await loadData();
  }

  // Saldo corrente — calculado em ordem cronológica, ignorando projeções
  const comSaldo = useMemo(() => {
    let saldo = 0;
    return lancamentos.map(l => {
      if (!l.is_projecao) {
        saldo += l.tipo === 'entrada' ? l.valor : -l.valor;
      }
      return { ...l, saldoApos: saldo };
    });
  }, [lancamentos]);

  // Filtros aplicados (exibição do mais recente primeiro)
  const filtrados = useMemo(() => {
    return [...comSaldo]
      .filter(l => {
        if (filtroTipo === 'entrada' && l.tipo !== 'entrada') return false;
        if (filtroTipo === 'saida' && l.tipo !== 'saida') return false;
        if (filtroTipo === 'projecao' && !l.is_projecao) return false;
        if (dataInicio && l.data < dataInicio) return false;
        if (dataFim && l.data > dataFim) return false;
        if (busca && !l.descricao.toLowerCase().includes(busca.toLowerCase())) return false;
        return true;
      })
      .reverse();
  }, [comSaldo, filtroTipo, dataInicio, dataFim, busca]);

  const saldoAtual = comSaldo.length ? comSaldo[comSaldo.length - 1].saldoApos : 0;
  const totalEntradas = lancamentos.filter(l => l.tipo === 'entrada' && !l.is_projecao).reduce((a,l) => a + l.valor, 0);
  const totalSaidas = lancamentos.filter(l => l.tipo === 'saida' && !l.is_projecao).reduce((a,l) => a + l.valor, 0);
  const totalProjecao = lancamentos.filter(l => l.is_projecao).reduce((a,l) => a + (l.tipo === 'entrada' ? l.valor : -l.valor), 0);

  return (
    <div className="livro-page fade-in">
      <div className="livro-header">
        <div>
          <h2 className="livro-title">Livro <span className="heading-accent">Caixa</span></h2>
          <p className="text-muted text-sm">Entradas e saídas com saldo corrente</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditLanc(null); setShowModal(true); }}>
          + Novo Lançamento
        </button>
      </div>

      {msg.text && <div className={`alert alert-${msg.type === 'error' ? 'error' : 'success'}`}>{msg.text}</div>}

      {/* Cards resumo */}
      <div className="livro-cards">
        <div className="livro-card">
          <div className="livro-card-label">Saldo Atual</div>
          <div className={`livro-card-valor font-mono ${saldoAtual >= 0 ? 'text-income' : 'text-expense'}`}>
            {fmt(saldoAtual)}
          </div>
        </div>
        <div className="livro-card">
          <div className="livro-card-label">Total Entradas</div>
          <div className="livro-card-valor font-mono text-income">{fmt(totalEntradas)}</div>
        </div>
        <div className="livro-card">
          <div className="livro-card-label">Total Saídas</div>
          <div className="livro-card-valor font-mono text-expense">{fmt(totalSaidas)}</div>
        </div>
        <div className="livro-card projecao">
          <div className="livro-card-label">Em Projeção</div>
          <div className={`livro-card-valor font-mono ${totalProjecao >= 0 ? 'text-income' : 'text-expense'}`}>
            {totalProjecao >= 0 ? '+' : ''}{fmt(totalProjecao)}
          </div>
        </div>
      </div>

      {/* Filtros estilo extrato bancário */}
      <div className="livro-filtros">
        <div className="filter-btns">
          {[
            { v:'todos', l:'Todos' },
            { v:'entrada', l:'↓ Entradas' },
            { v:'saida', l:'↑ Saídas' },
            { v:'projecao', l:'◇ Projeção' },
          ].map(f => (
            <button key={f.v} className={`btn btn-sm ${filtroTipo === f.v ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFiltroTipo(f.v)}>
              {f.l}
            </button>
          ))}
        </div>
        <div className="livro-busca-data">
          <input type="text" placeholder="Buscar descrição..." value={busca} onChange={e => setBusca(e.target.value)} className="livro-search-input" />
          <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} title="Data início" />
          <span className="text-muted text-xs">até</span>
          <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} title="Data fim" />
          {(dataInicio || dataFim || busca) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setDataInicio(''); setDataFim(''); setBusca(''); }}>Limpar</button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'60px 0', gap:12, color:'var(--text2)' }}>
          <div className="spinner" style={{ width:28, height:28 }} /><span>Carregando...</span>
        </div>
      ) : filtrados.length === 0 ? (
        <div className="livro-empty">
          <p style={{ fontSize:'2rem' }}>📖</p>
          <p>Nenhum lançamento encontrado.</p>
          <p className="text-muted text-sm">Clique em "+ Novo Lançamento" para começar.</p>
        </div>
      ) : (
        <div className="livro-extrato">
          {filtrados.map(l => (
            <div key={l.id} className={`livro-item ${l.is_projecao ? 'projecao' : ''}`}>
              <div className="livro-item-data font-mono text-xs text-muted">{fmtData(l.data)}</div>
              <div className="livro-item-desc">
                {l.descricao}
                {l.categoria && <span className="livro-categoria">{l.categoria}</span>}
                {l.is_projecao && <span className="badge-projecao" style={{ marginLeft:8 }}>Projeção</span>}
              </div>
              <div className={`livro-item-valor font-mono ${l.tipo === 'entrada' ? 'text-income' : 'text-expense'}`}>
                {l.tipo === 'entrada' ? '↓ +' : '↑ -'}{fmt(l.valor)}
              </div>
              <div className="livro-item-saldo font-mono text-xs text-muted">
                Saldo: {fmt(l.saldoApos)}
              </div>
              <div className="livro-item-acoes">
                <button className="acao-btn edit" onClick={() => { setEditLanc(l); setShowModal(true); }}>✎</button>
                <button className="acao-btn del" onClick={() => handleDelete(l.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <LancamentoModal lanc={editLanc} onSave={handleSave} onClose={() => { setShowModal(false); setEditLanc(null); }} />
      )}
    </div>
  );
}

function LancamentoModal({ lanc, onSave, onClose }) {
  const [form, setForm] = useState({
    data: lanc?.data || new Date().toISOString().slice(0,10),
    descricao: lanc?.descricao || '',
    tipo: lanc?.tipo || 'saida',
    valor: lanc?.valor || '',
    categoria: lanc?.categoria || '',
    is_projecao: lanc?.is_projecao || false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.descricao.trim()) { setError('Informe a descrição.'); return; }
    if (!form.valor || parseFloat(form.valor) <= 0) { setError('Informe um valor válido.'); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  }

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-box fade-in" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">{lanc ? 'Editar Lançamento' : 'Novo Lançamento'}</h3>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <form onSubmit={handleSubmit} className="modal-form">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <button type="button"
                className={`tipo-toggle ${form.tipo === 'entrada' ? 'active-entrada' : ''}`}
                onClick={() => set('tipo', 'entrada')}
              >↓ Entrada</button>
              <button type="button"
                className={`tipo-toggle ${form.tipo === 'saida' ? 'active-saida' : ''}`}
                onClick={() => set('tipo', 'saida')}
              >↑ Saída</button>
            </div>

            <div className="form-group">
              <label>Descrição</label>
              <input type="text" placeholder="Ex: Salário, Supermercado, Freelance" value={form.descricao}
                onChange={e => set('descricao', e.target.value)} autoFocus maxLength={100} />
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div className="form-group">
                <label>Valor (R$)</label>
                <input type="number" step="0.01" placeholder="0,00" value={form.valor}
                  onChange={e => set('valor', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Data</label>
                <input type="date" value={form.data} onChange={e => set('data', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label>Categoria (opcional)</label>
              <input type="text" placeholder="Ex: Trabalho, Mercado, Transporte" value={form.categoria}
                onChange={e => set('categoria', e.target.value)} maxLength={40} />
            </div>

            <label className="share-toggle-row" style={{ marginTop:4 }}>
              <input type="checkbox" checked={form.is_projecao} onChange={e => set('is_projecao', e.target.checked)} className="share-checkbox" />
              <span className="share-label">Marcar como projeção (não entra no saldo)</span>
            </label>

            {error && <div className="alert alert-error">{error}</div>}

            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <><span className="spinner" /> Salvando...</> : lanc ? 'Salvar Alterações' : 'Registrar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
}
